import { useEffect } from 'react';
import { API } from '../config/API';
import { forceLogout } from '../config/axiosInstance';

/**
 * Subscribes to the server's session-revocation stream so this tab logs out
 * the instant its device session is killed elsewhere — e.g. when the same
 * user logs in from another machine and single-session enforcement deactivates
 * this session. Works even while the tab is idle (no API requests needed).
 *
 * Auth rides on the existing httpOnly `access_token` cookie (EventSource sends
 * cookies with `withCredentials`); no Authorization header is possible here.
 *
 * Transient connection errors are intentionally ignored — EventSource
 * auto-reconnects, and a genuinely revoked session is signalled by the explicit
 * `revoked` event below (with the next API call's 401 → forceLogout as a
 * fallback). Forcing logout on every blip would log users out on flaky networks.
 */
export function useSessionStream(isAuthenticated: boolean) {
  useEffect(() => {
    if (!isAuthenticated) return;
    if (typeof EventSource === 'undefined') return;

    const source = new EventSource(`${API.BASE_URL}auth/session-stream`, {
      withCredentials: true,
    });

    const onRevoked = () => {
      source.close();
      forceLogout();
    };

    source.addEventListener('revoked', onRevoked);

    return () => {
      source.removeEventListener('revoked', onRevoked);
      source.close();
    };
  }, [isAuthenticated]);
}
